<template>
  <div class="achievement-image-selector">
    <div v-if="selectedImage" class="selected-image">
      <el-image :src="imageUrl(selectedImage)" fit="cover" class="selected-image__preview" />
      <div class="selected-image__details">
        <strong>{{ imageLabel(selectedImage) }}</strong>
        <details><summary>查看图片标识</summary>{{ selectedImage.id || modelValue }}</details>
        <el-tag :type="selectedImage.animation ? 'success' : 'info'" size="small">
          {{ selectedImage.animation ? '含动图' : '静态图' }}
        </el-tag>
      </div>
      <div class="selected-image__actions">
        <el-button size="small" :disabled="disabled" @click="openPicker">更换图片</el-button>
        <el-button size="small" :disabled="disabled" @click="clearSelection">清除</el-button>
      </div>
    </div>
    <div v-else class="selected-image selected-image--empty">
      <el-tag v-if="modelValue" type="warning">待重新选择有效图片</el-tag>
      <el-button size="small" type="primary" :disabled="disabled" @click="openPicker">选择奖励图片</el-button>
    </div>

    <el-dialog v-model="pickerOpen" title="从图库选择奖励图片" width="860px" append-to-body>
      <el-alert
        type="info"
        :closable="false"
        title="点击图片即可选择，无需填写图片 ID。这里只显示已上线、审核通过的图库图片。"
        class="picker-tip"
      />
      <div class="picker-toolbar">
        <a :href="$router.resolve({name: 'Index'}).href" target="_blank" rel="noopener" class="el-button">去图库上传新拼图 ↗</a>
        <el-button :loading="loading" @click="loadImages(1)">刷新图库</el-button>
      </div>
      <p class="picker-help">上传在新标签页完成，本页配图会保留。请在图库中完成上传、审核和上线，再回来刷新选择。</p>
      <div v-loading="loading" class="image-grid">
        <button
          v-for="image in images"
          :key="image.id"
          type="button"
          class="image-card"
          @click="selectImage(image)"
        >
          <el-image :src="imageUrl(image)" fit="cover" class="image-card__preview" />
          <span class="image-card__label">{{ imageLabel(image) }}</span>
        </button>
      </div>
      <el-empty v-if="!loading && !images.length" description="当前图库暂无可选图片" :image-size="72" />
      <el-pagination
        v-if="total > pageSize"
        layout="total, prev, pager, next"
        :total="total"
        :page-size="pageSize"
        :current-page="page"
        @current-change="loadImages"
      />
    </el-dialog>
  </div>
</template>

<script>
import axios from 'ts-axios-new';
import {ElMessage} from 'element-plus';

export default {
  name: 'AchievementImageSelector',
  props: {
    modelValue: {type: String, default: ''},
    disabled: Boolean,
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      pickerOpen: false,
      loading: false,
      images: [],
      selectedImage: null,
      page: 1,
      pageSize: 24,
      total: 0,
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        if (!value) {
          this.selectedImage = null;
          return;
        }
        if (this.selectedImage?.id !== value) this.loadSelectedImage(value);
      },
    },
  },
  methods: {
    imageUrl(image) {
      const thumbnail = image?.thumbnail || image?.resource || '';
      if (!thumbnail || /^https?:\/\//.test(thumbnail)) return thumbnail;
      const cdn = this.$root?.cdn_url || 'https://jigsaw-cdn.plutodesk.com';
      return `${String(cdn).replace(/\/$/, '')}/${String(thumbnail).replace(/^\//, '')}`;
    },
    imageLabel(image) {
      return image?.desc || image?.source || image?.online_date || '图库图片';
    },
    async loadSelectedImage(value) {
      if (!/^[a-f0-9]{24}$/i.test(value)) {
        this.selectedImage = null;
        return;
      }
      try {
        const response = await axios.get('/cms/v1/image', {params: {id: value, limit: 1}});
        this.selectedImage = response.data?.data?.imageList?.[0] || null;
      } catch (_) {
        this.selectedImage = null;
      }
    },
    openPicker() {
      this.pickerOpen = true;
      this.loadImages(1);
    },
    async loadImages(page) {
      this.page = Number(page) || 1;
      this.loading = true;
      try {
        const response = await axios.get('/cms/v1/image', {
          params: {
            skip: (this.page - 1) * this.pageSize,
            limit: this.pageSize,
            online: true,
            // 与奖励发布校验保持一致，只提供审核通过且有版权的图库图片。
            status: 'AUDITED',
            module: 'LIBRARY',
            unauthorized: false,
          },
        });
        this.images = response.data?.data?.imageList || [];
        this.total = Number(response.data?.data?.total || 0);
      } catch (error) {
        this.images = [];
        this.total = 0;
        ElMessage.error(error?.response?.data?.message || error?.message || '加载图库失败');
      } finally {
        this.loading = false;
      }
    },
    selectImage(image) {
      const imageId = String(image?.id || image?._id || '').trim().toLowerCase();
      if (!/^[a-f0-9]{24}$/.test(imageId)) {
        ElMessage.error('所选图片缺少有效资源标识，请重新选择');
        return;
      }
      this.selectedImage = image;
      this.$emit('update:modelValue', imageId);
      this.$emit('change', imageId);
      this.pickerOpen = false;
    },
    clearSelection() {
      this.selectedImage = null;
      this.$emit('update:modelValue', '');
      this.$emit('change', '');
    },
  },
};
</script>

<style scoped>
.achievement-image-selector {
  min-width: 0;
}

.selected-image {
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: 64px minmax(0, 1fr) auto;
  min-height: 32px;
}

.selected-image--empty {
  display: flex;
  gap: 8px;
}

.selected-image__preview {
  border-radius: 4px;
  height: 64px;
  width: 64px;
}

.selected-image__details {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  gap: 3px;
  min-width: 0;
}

.selected-image__details > span {
  color: var(--el-text-color-secondary);
  overflow-wrap: anywhere;
}

.selected-image__actions {
  display: flex;
  gap: 8px;
}

.selected-image__actions :deep(.el-button + .el-button) {
  margin-left: 0;
}

@media (max-width: 760px) {
  .selected-image {
    grid-template-columns: 64px minmax(0, 1fr);
  }

  .selected-image__actions {
    grid-column: 1 / -1;
  }
}

.picker-toolbar { display: flex; align-items: center; gap: 12px; }
.picker-toolbar a { text-decoration: none; }
.picker-help { color: var(--el-text-color-secondary); font-size: 13px; }
.selected-image__details details { font-size: 12px; overflow-wrap: anywhere; }
.picker-tip {
  margin-bottom: 12px;
}

.image-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  min-height: 180px;
}

.image-card {
  background: var(--el-bg-color);
  border: 1px solid var(--el-border-color);
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;
  padding: 0;
  text-align: left;
}

.image-card:hover {
  border-color: var(--el-color-primary);
}

.image-card__preview {
  display: block;
  height: 100px;
  width: 100%;
}

.image-card__label {
  display: block;
  overflow: hidden;
  padding: 6px 8px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
